import {
  ContentCopy,
  Delete,
  Refresh,
  Add,
  CheckCircle,
  ErrorOutline,
  HourglassEmpty,
  Lock,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { DEFAULT_DOMAIN_LIMIT } from "../../../constants";
import { hostname, parseError } from "../../../utils";

interface TrackingDomainsPanelProps {
  companyId: string;
  getToken: () => Promise<string>;
  title?: string;
  description?: string;
  addButtonLabel?: string;
  emptyMessage?: string;
}

interface TrackingDomain {
  id: string;
  subdomain: string;
  targetDomain: string;
  status: string;
  sslEnabled?: boolean;
  verifiedDate?: string;
  lastVerificationDate?: string;
  lastError?: string;
}

const TrackingDomainsPanel: React.FC<TrackingDomainsPanelProps> = ({
  companyId,
  getToken,
  title = "Custom Tracking Domains",
  description = "Custom tracking domains allow you to use your own branded subdomain (e.g., track.yourbrand.com) for email open and click tracking. This improves deliverability and builds trust by showing branded links instead of third-party URLs.",
  addButtonLabel = "Add Tracking Domain",
  emptyMessage = "No tracking domains configured",
}) => {
  const [domains, setDomains] = useState<TrackingDomain[]>([]);
  const [domainLimit, setDomainLimit] = useState(DEFAULT_DOMAIN_LIMIT);
  const [loading, setLoading] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/tracking-domains`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setDomains(response.data.items || []);
      if (response.data.domainLimit !== undefined) {
        setDomainLimit(response.data.domainLimit);
      }
    } catch (err) {
      console.error("Failed to fetch tracking domains:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDnsInfo = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/tracking-domains/dns-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIpAddress(response.data.ipAddress || "");
    } catch (err) {
      console.error("Failed to fetch DNS info:", err);
    }
  };

  useEffect(() => {
    fetchDomains();
    fetchDnsInfo();
  }, [companyId]);

  const handleCreate = async () => {
    if (!newSubdomain.trim()) return;

    setCreating(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/tracking-domains`,
        {
          companyId,
          subdomain: newSubdomain.trim().toLowerCase(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNewSubdomain("");
      setShowAddForm(false);

      if (response.data.approximatedRegistered === false) {
        setError("Domain created but SSL provisioning failed. You can still set up DNS and try verifying later.");
      }

      await fetchDomains();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setCreating(false);
    }
  };

  const handleVerify = async (domainId: string) => {
    setVerifyingId(domainId);
    setError(null);
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/tracking-domains/${domainId}/verify`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.data.verified) {
        setError(response.data.message || "Verification failed");
      }
      await fetchDomains();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setVerifyingId(null);
    }
  };

  const handleDelete = async (domainId: string) => {
    try {
      const token = await getToken();
      await axios.delete(`${hostname}/tracking-domains/${domainId}`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchDomains();
    } catch (err) {
      setError(parseError(err));
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusChip = (domain: TrackingDomain) => {
    if (domain.status === "verified" && domain.sslEnabled) {
      return (
        <Chip
          icon={<Lock />}
          label="SSL Active"
          color="primary"
          size="small"
        />
      );
    } else if (domain.status === "verified") {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Verified"
          color="primary"
          size="small"
        />
      );
    } else if (domain.status === "failed") {
      return (
        <Chip
          icon={<ErrorOutline />}
          label="Failed"
          color="error"
          size="small"
        />
      );
    }
    return (
      <Chip
        icon={<HourglassEmpty />}
        label="Pending"
        color="warning"
        size="small"
      />
    );
  };

  const isAtLimit = domains.length >= domainLimit;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isAtLimit && !showAddForm && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have reached the maximum of {domainLimit} custom domains.
        </Alert>
      )}

      {!showAddForm ? (
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={() => setShowAddForm(true)}
          sx={{ mb: 2 }}
          disabled={isAtLimit}
        >
          {addButtonLabel}
        </Button>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Add New Domain
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              label="Domain"
              placeholder="pages.yourdomain.com"
              value={newSubdomain}
              onChange={(e) => setNewSubdomain(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              helperText="Enter your custom domain (e.g., pages.example.com)"
            />
            <LoadingButton
              loading={creating}
              variant="contained"
              onClick={handleCreate}
              disabled={!newSubdomain.trim()}
            >
              Add
            </LoadingButton>
            <Button
              variant="text"
              onClick={() => {
                setShowAddForm(false);
                setNewSubdomain("");
              }}
            >
              Cancel
            </Button>
          </Box>
          {ipAddress && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                After adding, create an <strong>A record</strong> in your
                DNS settings pointing your domain to:{" "}
                <strong>{ipAddress}</strong>
                <Tooltip title="Copy to clipboard">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(ipAddress)}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
            </Alert>
          )}
        </Paper>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Domain</TableCell>
              <TableCell>A Record (IP Address)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : domains.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              domains.map((domain) => (
                <TableRow key={domain.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {domain.subdomain}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {domain.targetDomain}
                      </Typography>
                      <Tooltip title="Copy IP address">
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(domain.targetDomain)}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(domain)}
                    {domain.lastError && domain.status !== "verified" && (
                      <Tooltip title={domain.lastError}>
                        <ErrorOutline
                          fontSize="small"
                          color="error"
                          sx={{ ml: 1, verticalAlign: "middle" }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Verify DNS & SSL">
                      <IconButton
                        size="small"
                        onClick={() => handleVerify(domain.id)}
                        disabled={verifyingId === domain.id}
                      >
                        <Refresh
                          fontSize="small"
                          sx={{
                            animation:
                              verifyingId === domain.id
                                ? "spin 1s linear infinite"
                                : "none",
                            "@keyframes spin": {
                              "0%": { transform: "rotate(0deg)" },
                              "100%": { transform: "rotate(360deg)" },
                            },
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(domain.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {domains.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Setup Instructions:</strong>
            <ol style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li>Go to your domain's DNS settings</li>
              <li>Create an <strong>A record</strong> for your custom domain</li>
              <li>Point it to the IP address shown in the table above</li>
              <li>Wait for DNS propagation (may take up to 24-48 hours)</li>
              <li>Click the refresh icon to verify DNS and SSL status</li>
              <li>SSL certificates are automatically provisioned once DNS is configured</li>
            </ol>
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default TrackingDomainsPanel;
